import React, { useEffect } from 'react';
import cx from 'classnames';
import { Form } from 'antd';
import CardRadioGroup from '../../components/CardRadioGroup';
import HiddenFormField from '../../components/HiddenFormField';
import SquareCardForm from '../../components/SquareCardForm';
import { paymentBridge } from '../../paymentBridge';
import s from '../../s.module.less';
import sp from './s.module.less';

const CheckCircle = () => (
    <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#16A34A"
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
    >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <path d="m9 11 3 3L22 4" />
    </svg>
);

// 品牌徽标（原型 cardBrandIcon）：VISA 深蓝块 / Mastercard 双圆 / 兜底灰块
const CardBrandBadge = ({ brand }: { brand?: string }) => {
    const b = String(brand || '').toLowerCase();
    if (b === 'visa') return <span className={cx(sp.brand, sp.visa)}>VISA</span>;
    if (b === 'mastercard') {
        return (
            <span className={cx(sp.brand, sp.mc)}>
                <i className={sp.mc1} />
                <i className={sp.mc2} />
            </span>
        );
    }
    return (
        <span className={cx(sp.brand, sp.generic)}>
            <i />
        </span>
    );
};

// Step 8 — Payment（原型 billing.html renderPayment()，本期只做 Credit card，不做 bank/ACH）。
// 两态：Klarity 账户已有卡（synced 绿框，默认）↔ 手动录卡（真实 Square 表单）。
// 与其它步骤不同，本步在向导里是硬门禁：Continue 时 tokenize + 保存，成功才放行（见 index.tsx）
export default function StepPayment() {
    const form = Form.useFormInstance();
    // useWatch 只作重渲染触发器，值从原始 store 读（与 index.tsx 对 billing/rendering 的用法一致）
    Form.useWatch(['payment'], form);
    const p = form.getFieldValue(['payment']) || {};
    const hasCardOnFile = !!p.cardOnFile?.last4;
    const showManual = !!p.useManualCard || !hasCardOnFile;

    // 没有 card on file 时只能手动录卡：把 useManualCard 落进表单，
    // 保存（savePaymentMethodStep）和 Review 必填判定（reviewSpec）与 UI 保持同一口径
    useEffect(() => {
        if (!hasCardOnFile && !p.useManualCard) {
            form.setFieldValue(['payment', 'useManualCard'], true);
        }
    });

    // 手动卡保存成功后（savePaymentMethodStep 经 bridge 回调）：新卡写回 cardOnFile 并收起手动态
    useEffect(() => {
        paymentBridge.onSaved = (card) => {
            form.setFieldValue(['payment', 'cardOnFile'], card);
            form.setFieldValue(['payment', 'useManualCard'], false);
        };
        return () => {
            paymentBridge.onSaved = null;
        };
    }, [form]);

    return (
        <div className={s.wizSection} data-wiz-section>
            {/* useManualCard / cardOnFile 由 setFieldValue 驱动，挂空控件完成注册（useWatch 的坑） */}
            <Form.Item name={['payment', 'useManualCard']} noStyle>
                <HiddenFormField />
            </Form.Item>
            <Form.Item name={['payment', 'cardOnFile']} noStyle>
                <HiddenFormField />
            </Form.Item>
            <Form.Item name={['payment', 'method']} initialValue="card" noStyle>
                <CardRadioGroup
                    options={[
                        {
                            value: 'card',
                            title: 'Credit or debit card',
                            desc: 'Use the card already on file with your Klarity account, or a different one.',
                        },
                    ]}
                />
            </Form.Item>
            <div className={sp.cardBody}>
                {showManual ? (
                    <>
                        <SquareCardForm />
                        {hasCardOnFile && (
                            <div className={sp.switchRow}>
                                <button
                                    type="button"
                                    className={sp.linkBtn}
                                    onClick={() => form.setFieldValue(['payment', 'useManualCard'], false)}
                                >
                                    Use my card on file instead
                                </button>
                            </div>
                        )}
                    </>
                ) : (
                    <>
                        <div className={sp.linked}>
                            <CheckCircle />
                            <CardBrandBadge brand={p.cardOnFile.brand} />
                            <span className={sp.linkedName}>{p.cardOnFile.brand}</span>
                            <span className={sp.linkedNum}>····{p.cardOnFile.last4}</span>
                            <button
                                type="button"
                                className={cx(sp.linkBtn, sp.pushRight)}
                                onClick={() => form.setFieldValue(['payment', 'useManualCard'], true)}
                            >
                                Use a different card
                            </button>
                        </div>
                        <p className={sp.foot}>This card will be charged for Klarity&apos;s managed billing fee.</p>
                    </>
                )}
            </div>
        </div>
    );
}
